import { clock12 } from "./comingUp";

/**
 * Issue #1150 (D225 item 6) — a planned match after its day: when it is "has
 * been and gone", the card's words, and the link that opens the scorecard for
 * it. Drawn in `PlanMatchAfter.dc.html` (round 10), mirroring the training
 * counterpart (`needsReflection`, `trainingStatus.js`).
 *
 * Pure, with `now` injected, so the midnight and time-zone cases are pinned
 * without a mount (`plannedMatches.test.js`).
 */

/** The query param `/performance?finish=<matchId>` is read from. */
export const FINISH_PARAM = "finish";

/** The board's sentence under the match line. */
export const OVERDUE_SENTENCE = "This match has been and gone. Add how it went and it moves into your record.";
export const OVERDUE_BADGE = "Needs your figures";
export const OVERDUE_ACTION = "Add how it went";

export const isPlannedMatch = (m) => m?.status === "planned";

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME = /^(\d{1,2}):(\d{2})/;

/**
 * Is this planned match past its date and time, in the BROWSER'S local time
 * (#1093: the player's calendar day, never UTC's)? With a start time it is
 * overdue once that minute has passed; with none, once its whole local day is
 * over. A played match, or a row whose date or time cannot be read, is never
 * overdue: the card is a prompt, and it must not be raised on data we cannot
 * read.
 */
export function isOverdue(match, now = new Date()) {
  if (!isPlannedMatch(match)) return false;
  const d = DATE.exec(String(match.match_date || ""));
  if (!d) return false;
  const [y, mo, day] = [Number(d[1]), Number(d[2]), Number(d[3])];
  const raw = match.match_time;
  let due;
  if (raw) {
    const t = TIME.exec(String(raw));
    if (!t) return false;
    due = new Date(y, mo - 1, day, Number(t[1]), Number(t[2]));
  } else {
    due = new Date(y, mo - 1, day + 1);
  }
  if (Number.isNaN(due.getTime())) return false;
  // A timed match is due once its minute has passed; an untimed one at the
  // first instant of the next local day.
  return raw ? now.getTime() > due.getTime() : now.getTime() >= due.getTime();
}

/** The overdue planned matches, oldest first (the longest-waiting leads). */
export function overdueMatches(matches, now = new Date()) {
  if (!Array.isArray(matches)) return [];
  return matches
    .filter((m) => isOverdue(m, now))
    .sort(
      (a, b) =>
        String(a.match_date).localeCompare(String(b.match_date)) ||
        String(a.match_time || "99:99").localeCompare(String(b.match_time || "99:99")),
    );
}

/** "Fri 16 Oct · 2:00 pm · [GROUND] · [TEAM]", skipping what is not there. */
export function matchLine(m) {
  // Always the dated form: a match that has been and gone is never "Today"
  // or "Tomorrow" (en-NZ may render "Fri, 16 Oct"; the board has no comma).
  const day = new Date(`${m.match_date}T00:00:00Z`)
    .toLocaleDateString("en-NZ", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" })
    .replace(",", "");
  const time = clock12(m.match_time);
  return [time ? `${day} \u00b7 ${time}` : day, m.ground, m.team].filter(Boolean).join(" \u00b7 ");
}

/** `/performance?finish=<matchId>` — Add how it went. */
export const finishMatchPath = (matchId) => `/performance?${FINISH_PARAM}=${encodeURIComponent(matchId)}`;

/** The match id `?finish=` names, or `null` when absent or blank. Opaque, never coerced. */
export function finishMatchId(search) {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const id = params?.get(FINISH_PARAM);
  return id && id.trim() !== "" ? id : null;
}
